import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { debounce, isString } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { Stack } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { MobileDatePicker } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  Dialog,
  Select,
  Divider,
  Tooltip,
  MenuItem,
  InputLabel,
  Typography,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
  FormHelperText,
  InputAdornment
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useCustomFields } from 'src/hooks/use-custom-fields';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { Currency } from 'src/services/utils/utils.types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { useGetJobByIdQuery } from 'src/services/jobs/jobs.service';
import { closePlacementEditor } from 'src/store/slices/jobs/jobsSlice';
import { useGetCurrenciesQuery } from 'src/services/utils/utils.service';
import { showCompanyEditor } from 'src/store/slices/companies/companiesSlice';
import { useGetMyCompaniesQuery } from 'src/services/connections/connections.service';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import { FeeType, PeriodType, EmploymentType, PaymentFrequency } from 'src/services/jobs/jobs.types';
import {
  CreatePlacementDto,
  ProfitCalculationType
} from 'src/services/applications/applications.types';
import {
  useGetJobCandidatesQuery,
  useGetPlacementByIdQuery,
  useCreatePlacementMutation,
  useUpdatePlacementMutation,
  useLazyGetApplicationByIdQuery
} from 'src/services/applications/applications.service';

import Iconify from 'src/components/iconify';
import FormProvider, {
  RHFSelect,
  RHFCheckbox,
  RHFTextField,
  RHFAutocomplete,
} from 'src/components/hook-form';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

interface CompanyOption {
  id: string;
  company_name: string;
}

interface ContactOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

type PlacementEditorProps = {
  open: boolean;
};

export default function PlacementEditor({ open }: PlacementEditorProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const authContext = useAuthContext();

  const tenantType = useOrgTenant();

  const dispatch = useAppDispatch();

  const placementId = useAppSelector((state) => state.jobs.placementEditorState.placementId);
  const jobId = useAppSelector((state) => state.jobs.placementEditorState.jobId);
  const applicationId = useAppSelector((state) => state.jobs.placementEditorState.applicationId);

  const isEditMode = Boolean(placementId);
  const [selectedApplication, setSelectedApplication] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [companySearchQuery, setCompanySearchQuery] = useState<string>('');
  const [contactSearchQuery, setContactSearchQuery] = useState<string>('');

  const { data: candidatesData, isLoading: isLoadingCandidates } = useGetJobCandidatesQuery({
    jobId,
    params: { page: 1, per_page: 100 },
  }, { skip: isEditMode });

  const { data: placementData, isLoading: isLoadingPlacement } = useGetPlacementByIdQuery(
    placementId as string,
    { skip: !isEditMode || !placementId }
  );

  const { currentData: companies, isLoading: isConnectionsLoading, isFetching: isCompaniesFetching } = useGetMyCompaniesQuery(
    {
      params: {
        page: 1,
        per_page: 10,
        search_query: companySearchQuery
      },
    }
  );

  const { currentData: contacts, isLoading: isContactsLoading } = useGetOrganizationContactsQuery(
    {
      org_id: authContext.organization.id,
      page: {
        page: 1,
        per_page: 10,
      },
      search_query: contactSearchQuery
    }
  );

  const [retrieveApplication] = useLazyGetApplicationByIdQuery();

  const { currentData: currencies, isLoading: isLoadingCurrencies } = useGetCurrenciesQuery();

  // job data
  const { data: jobData, isLoading: isLoadingJob } = useGetJobByIdQuery(
    jobId as string,
    { skip: !jobId || isEditMode }
  );

  const [createPlacement, { isLoading: isCreatingPlacement }] = useCreatePlacementMutation();
  const [updatePlacement, { isLoading: isUpdatingPlacement }] = useUpdatePlacementMutation();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.PLACEMENT,
    entityId: placementId || undefined,
  });

  const PlacementValidationSchema = Yup.object().shape({
    job_application_id: Yup.string().required(t('validation.required')),
    candidate_first_name: Yup.string().required(t('validation.required')),
    candidate_last_name: Yup.string().required(t('validation.required')),
    candidate_email: Yup.string().email(t('validation.email_format')).required(t('validation.required')),
    job_title: Yup.string().required(t('validation.required')),

    employer_company: Yup.mixed<CompanyOption>().nullable(),

    recruiter_first_name: Yup.string(),
    recruiter_last_name: Yup.string(),
    recruiter_company: Yup.mixed<CompanyOption>().nullable(),

    employment_type: Yup.string().required(t('validation.required')),
    duration_days: Yup.number().nullable(),
    billable_days: Yup.number().nullable(),
    start_date: Yup.date().nullable(),
    end_date: Yup.date()
      .nullable()
      .when('start_date', (start_date, schema) =>
        start_date && start_date[0]
          ? schema.min(start_date[0], t('validation.min_end_date'))
          : schema
      ),

    // financial information
    base_salary: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    total_remuneration: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    pay_rate: Yup.string(),
    bill_rate: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    margin: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    total_margin: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),

    profit_calculation_type: Yup.string().nullable(),
    hours_per_day: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    additional_entitlements: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    placement_fee_value: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),
    placement_fee_type: Yup.string().nullable(),
    is_prorated_fee: Yup.boolean(),
    proration_percentage: Yup.number().nullable().when(['is_prorated_fee', 'employment_type'], ([is_prorated_fee, employment_type], schema) =>
      employment_type !== EmploymentType.CONTRACT && is_prorated_fee ? schema.required(t('validation.required')).min(1, t('validation.min', { min: 1 })).max(100, t('validation.max', { max: 100 })) : schema
    ),
    on_costs: Yup.number().nullable().transform((value) => (Number.isNaN(value) ? null : value)),

    currency: Yup.object()
      .shape({
        id: Yup.string().defined(),
        name: Yup.string().defined(),
        code: Yup.string().defined(),
      })
      .nullable(),

    // billing information
    billing_contact: Yup.mixed<ContactOption>().nullable(),
    billing_contact_first_name: Yup.string(),
    billing_contact_last_name: Yup.string(),
    billing_contact_email: Yup.string().email(t('validation.email_format')),
    billing_invoice_number: Yup.string(),
    billing_po_number: Yup.string(),
    payment_terms: Yup.string(),
    payment_frequency: Yup.string().nullable(),

    send_invoice_on: Yup.date().nullable(),

    // important dates
    offer_accepted_at: Yup.date().nullable(),
    contract_signed_at: Yup.date().nullable(),
    contract_sent_at: Yup.date().nullable(),

    // additional information
    offer_information: Yup.string(),
    related_offer_id: Yup.string(),
  });

  const defaultValues = {
    job_application_id: '',
    candidate_first_name: '',
    candidate_last_name: '',
    candidate_email: '',

    job_title: '',
    employment_type: EmploymentType.PERMANENT,

    employer_company: null,

    recruiter_company: null,
    recruiter_first_name: '',
    recruiter_last_name: '',

    // job
    duration_days: 0,
    billable_days: 0,
    start_date: null,
    end_date: null,

    // financial information
    base_salary: 0,
    total_remuneration: null as number | null,
    pay_rate: '',
    bill_rate: null as number | null,
    margin: 0 as number,
    total_margin: 0 as number,
    hours_per_day: null as number | null,
    additional_entitlements: null as number | null,

    placement_fee_value: null as number | null,
    placement_fee_type: "",
    is_prorated_fee: false,
    proration_percentage: null as number | null,
    on_costs: null as number | null,
    profit_calculation_type: ProfitCalculationType.MARGIN as ProfitCalculationType,

    currency: null,

    // billing information
    billing_contact: null,
    billing_contact_first_name: '',
    billing_contact_last_name: '',
    billing_contact_email: '',
    billing_invoice_number: '',
    billing_po_number: '',
    payment_terms: '',
    payment_frequency: null as string | null,

    send_invoice_on: null,

    // important dates
    offer_accepted_at: null,
    contract_signed_at: null,
    contract_sent_at: null,

    // additional information
    offer_information: '',
    related_offer_id: '',
  };

  const methods = useForm({
    resolver: yupResolver(PlacementValidationSchema),
    defaultValues,
  });

  const handleClose = () => {
    setSelectedApplication('');
    reset(defaultValues);
    dispatch(closePlacementEditor());
  }

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  console.log(errors)

  const employerCompany = watch('employer_company');
  const recruiterCompany = watch('recruiter_company');
  const employmentType = watch('employment_type');
  const billingContact = watch('billing_contact');

  // Watch financial fields for calculations
  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const baseSalary = watch('base_salary');
  const additionalEntitlements = watch('additional_entitlements');
  const totalRemuneration = watch('total_remuneration');
  const onCosts = watch('on_costs');
  const placementFeeValue = watch('placement_fee_value');
  const placementFeeType = watch('placement_fee_type');
  const profitCalculationType = watch('profit_calculation_type');
  const rateMargin = watch('margin');
  const payRate = watch('pay_rate');
  const billableDays = watch('billable_days');
  const isProratedFee = watch('is_prorated_fee');
  const prorationPercentage = watch('proration_percentage');
  const hoursPerDay = watch('hours_per_day');

  // handle application selection
  const handleApplicationChange = useCallback(async (appId: string) => {
    setSelectedApplication(appId);
    setValue('job_application_id', appId);

    // find the selected application
    const application = await retrieveApplication(appId).unwrap();

    if (application) {
      // Auto-fill form fields based on the selected application
      setValue('candidate_first_name', application.attached_profile.first_name);
      setValue('candidate_last_name', application.attached_profile.last_name);
      setValue('candidate_email', application.attached_profile.email || '');
      setValue('employment_type', application.job.employment_type || EmploymentType.PERMANENT);
    }
  }, [retrieveApplication, setValue]);

  useEffect(() => {
    if (open && !isEditMode) {
      if (applicationId) {
        handleApplicationChange(applicationId);
      }

      if (jobData) {
        setValue('job_title', jobData.title);
        setValue('employment_type', jobData.employment_type as EmploymentType);
        setValue('start_date', jobData.start_date ? new Date(jobData.start_date) : null);
      }
    }
  }, [open, isEditMode, applicationId, jobData, handleApplicationChange, setValue]);

  useEffect(() => {
    if (billingContact) {
      setValue('billing_contact_first_name', billingContact.first_name || '', { shouldTouch: true });
      setValue('billing_contact_last_name', billingContact.last_name || '', { shouldTouch: true });
      setValue('billing_contact_email', billingContact.email || '', { shouldTouch: true });
    }
  }, [billingContact, setValue]);

  // calculate duration_days when start_date and end_date change
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // only calculate if end date is after start date
      if (end >= start) {
        // calculate difference in days
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days

        setValue('duration_days', diffDays);
        setValue('billable_days', diffDays);
      }
    }
  }, [startDate, endDate, setValue]);

  // calculate total_remuneration when base_salary or additional_entitlements change
  useEffect(() => {
    const salary = baseSalary || 0;
    const entitlements = additionalEntitlements || 0;

    const total = salary + entitlements;

    // only update if there's a change to avoid infinite loops
    if (total !== totalRemuneration) {
      setValue('total_remuneration', parseFloat(total.toFixed(2)));
    }
  }, [baseSalary, additionalEntitlements, totalRemuneration, setValue]);

  // calculate bill_rate based on total_remuneration and placement fee
  useEffect(() => {
    const totalRem = totalRemuneration || 0;
    const onCostsValue = onCosts || 0;
    let billRate = 0;
    let billMargin = 0;

    if (employmentType === EmploymentType.CONTRACT) {

      if (placementFeeValue) {

        if (profitCalculationType === ProfitCalculationType.MARGIN) {
          // ff fee type is percentage, calculate percentage of total remuneration
          if (totalRem > 0) {
            if (placementFeeType === FeeType.PERCENTAGE) {
              billRate = (totalRem + onCostsValue) / (1 - (placementFeeValue / 100));
            } else {
              // otherwise use the direct value
              billRate = (totalRem + onCostsValue) + placementFeeValue;
            }
          }

          const margin = billRate - (totalRem + onCostsValue);

          billRate = parseFloat(billRate.toFixed(2));
          billMargin = parseFloat(margin.toFixed(2));
        }
        else if (profitCalculationType === ProfitCalculationType.MARKUP) {
          if (placementFeeType === FeeType.PERCENTAGE && totalRem > 0) {
            const feeAmount = (totalRem + onCostsValue) * (placementFeeValue / 100);
            billRate = totalRem + onCostsValue + feeAmount;

          } else {
            // otherwise use the direct value
            billRate = (totalRem + onCostsValue) + placementFeeValue;
          }

          const margin = billRate - (totalRem + onCostsValue);

          billRate = parseFloat(billRate.toFixed(2));
          billMargin = parseFloat(margin.toFixed(2));
        }

      } else {
        // ff no placement fee, bill rate equals total remuneration
        billRate = parseFloat(totalRem.toFixed(2));
      }
    }

    setValue('bill_rate', billRate);
    setValue('margin', billMargin);

  }, [
    totalRemuneration,
    placementFeeValue,
    placementFeeType,
    setValue,
    employmentType,
    baseSalary,
    additionalEntitlements,
    onCosts,
    profitCalculationType
  ]);

  // calculate total margin
  useEffect(() => {

    const totalRem = totalRemuneration || 0;
    const margin = rateMargin || 0;
    let totalMargin = 0;

    if (totalRem > 0 && placementFeeValue && placementFeeValue > 0) {
      if (employmentType === EmploymentType.CONTRACT) {

        // for contract roles, pay rate, billable days and hours per day (if pay rate is day) must be provided 
        if (margin > 0 && payRate && billableDays && billableDays > 0) {

          // if pay rate is day, hours per day must be provided and greater than 0
          if (payRate === PeriodType.HOUR && !hoursPerDay || hoursPerDay === 0) {
            setValue('total_margin', 0);
            return;
          }

          // find the pay rate multiplier
          let payRateMultiplier = 1;
          if (payRate === PeriodType.HOUR) {
            payRateMultiplier = hoursPerDay || 1;
          }

          totalMargin = margin * billableDays * payRateMultiplier;
        }
      }
      else if (placementFeeType === FeeType.FIXED) {
        totalMargin = placementFeeValue || 0;
      }
      else {
        const fullPlacementFee = totalRem * ((placementFeeValue || 0) / 100);
        if (isProratedFee && prorationPercentage) {
          totalMargin = fullPlacementFee * (prorationPercentage / 100);
        } else {
          totalMargin = fullPlacementFee;
        }
      }
    }

    setValue('total_margin', totalMargin);
  }, [
    rateMargin,
    employmentType,
    payRate,
    hoursPerDay,
    billableDays,
    profitCalculationType,
    isProratedFee,
    prorationPercentage,
    placementFeeValue,
    placementFeeType,
    totalRemuneration,
    setValue
  ]);

  useEffect(() => {
    if (payRate && payRate === PeriodType.DAY) {
      setValue('hours_per_day', null);
    }
  }, [payRate, setValue]);

  // handle form submission
  const onSubmit = async (data: any) => {
    try {

      setErrorMessage(null);

      // Validate custom fields first
      const customFieldValidation = customFields.validate();
      if (!customFieldValidation.isValid) {
        return;
      }

      // format dates for API
      const formattedData: CreatePlacementDto = {
        job_application_id: data.job_application_id,
        candidate_first_name: data.candidate_first_name,
        candidate_last_name: data.candidate_last_name,
        candidate_email: data.candidate_email,
        job_title: data.job_title,
        employment_type: data.employment_type,

        employer_company_id: data.employer_company ? data.employer_company.id : undefined,

        recruiter_company_id: data.recruiter_company ? data.recruiter_company.id : undefined,
        recruiter_first_name: data.recruiter_first_name || undefined,
        recruiter_last_name: data.recruiter_last_name || undefined,

        duration_days: data.duration_days || undefined,
        billable_days: data.billable_days || undefined,
        base_salary: data.base_salary || undefined,
        total_remuneration: data.total_remuneration || 0,
        pay_rate: data.pay_rate || undefined,
        bill_rate: data.bill_rate || undefined,
        margin: data.margin || 0,// auto calculated therefore must be null
        total_margin: data.total_margin || 0, // auto calculated therefore must be null
        hours_per_day: data.hours_per_day || 0,
        additional_entitlements: data.additional_entitlements || undefined,

        placement_fee_value: data.placement_fee_value || undefined,
        placement_fee_type: data.placement_fee_type || undefined,
        is_prorated_fee: data.is_prorated_fee || false,
        proration_percentage: data.proration_percentage || undefined,
        on_costs: data.on_costs || undefined,
        profit_calculation_type: data.profit_calculation_type || undefined,

        currency_code: data.currency ? data.currency.code : undefined,

        billing_contact_id: data.billing_contact ? data.billing_contact.id : undefined,
        billing_contact_first_name: data.billing_contact_first_name || undefined,
        billing_contact_last_name: data.billing_contact_last_name || undefined,
        billing_contact_email: data.billing_contact_email || undefined,
        billing_invoice_number: data.billing_invoice_number || undefined,
        billing_po_number: data.billing_po_number || undefined,
        payment_terms: data.payment_terms || undefined,
        payment_frequency: data.payment_frequency || undefined,

        start_date: data.start_date ? new Date(data.start_date) : undefined,
        end_date: data.end_date ? new Date(data.end_date) : undefined,
        send_invoice_on: data.send_invoice_on ? data.send_invoice_on : undefined,

        offer_accepted_at: data.offer_accepted_at ? new Date(data.offer_accepted_at) : undefined,
        contract_signed_at: data.contract_signed_at ? new Date(data.contract_signed_at) : undefined,
        contract_sent_at: data.contract_sent_at ? new Date(data.contract_sent_at) : undefined,

        offer_information: data.offer_information || undefined,
        related_offer_id: data.related_offer_id || undefined,

        custom_fields: customFields.getData()
      };

      if (isEditMode) {

        if (!placementId) return;

        await updatePlacement({
          placementId: placementId as string,
          updateDto: formattedData,
        }).unwrap();

        enqueueSnackbar(t('jobs.detail.placements.api.update.success'), {
          variant: 'success',
        });
      } else {

        if (!jobId) return;

        await createPlacement({
          jobId,
          createDto: formattedData,
        }).unwrap();

        enqueueSnackbar(t('jobs.detail.placements.api.create.success'), {
          variant: 'success',
        });
      }

      handleClose();
    } catch (error) {
      if (isEditMode) {
        enqueueSnackbar(t('jobs.detail.placements.api.update.default_error'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('jobs.detail.placements.api.create.default_error'), { variant: 'error' });
      }
    }
  };

  const handleAddNewCompany = () => {
    dispatch(showCompanyEditor());
  }

  const onCompanySearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  const onContactSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  // populate form with existing data in edit mode
  useEffect(() => {
    if (isEditMode) {
      if (placementData) {

        let employer = null;
        if (placementData.employer_company) {
          employer = {
            id: placementData.employer_company.id,
            company_name: placementData.employer_company.company_name,
          };
        }

        let recruiter = null;
        if (placementData.recruiter_company) {
          recruiter = {
            id: placementData.recruiter_company.id,
            company_name: placementData.recruiter_company.company_name,
          };
        }

        reset({
          job_application_id: placementData.application.id || '',
          candidate_first_name: placementData.candidate_first_name || '',
          candidate_last_name: placementData.candidate_last_name || '',
          candidate_email: placementData.candidate_email || '',
          job_title: placementData.job_title || '',
          employment_type: placementData.employment_type || EmploymentType.PERMANENT,

          employer_company: employer,

          recruiter_company: recruiter,
          recruiter_first_name: placementData.recruiter_first_name || '',
          recruiter_last_name: placementData.recruiter_last_name || '',

          duration_days: placementData.duration_days || 0,
          billable_days: placementData.billable_days || 0,
          start_date: placementData.start_date ? new Date(placementData.start_date) : null,
          end_date: placementData.end_date ? new Date(placementData.end_date) : null,

          base_salary: placementData.base_salary || null,
          total_remuneration: placementData.total_remuneration || null,
          pay_rate: placementData.pay_rate || '',
          bill_rate: placementData.bill_rate || null,
          margin: placementData.margin || null,
          profit_calculation_type: placementData.profit_calculation_type || ProfitCalculationType.MARGIN,
          hours_per_day: placementData.hours_per_day || null,
          additional_entitlements: placementData.additional_entitlements || null,

          placement_fee_value: placementData.placement_fee_value ? Number(placementData.placement_fee_value) : null,
          placement_fee_type: placementData.placement_fee_type || null,
          is_prorated_fee: placementData.is_prorated_fee || false,
          proration_percentage: placementData.proration_percentage || null,
          on_costs: placementData.on_costs || null,

          currency: placementData.currency ? {
            id: placementData.currency.id,
            name: placementData.currency.name,
            code: placementData.currency.code,
          } : null,

          billing_contact: placementData.billing_contact ? {
            id: placementData.billing_contact.id,
            first_name: placementData.billing_contact.first_name,
            last_name: placementData.billing_contact.last_name,
            email: placementData.billing_contact.email || '',
          } : null,
          billing_contact_first_name: placementData.billing_contact_first_name || '',
          billing_contact_last_name: placementData.billing_contact_last_name || '',
          billing_contact_email: placementData.billing_contact_email || '',
          billing_invoice_number: placementData.billing_invoice_number || '',
          billing_po_number: placementData.billing_po_number || '',
          payment_terms: placementData.payment_terms || '',
          payment_frequency: placementData.payment_frequency || null,

          send_invoice_on: placementData.send_invoice_on ? new Date(placementData.send_invoice_on) : null,

          offer_accepted_at: placementData.offer_accepted_at ? new Date(placementData.offer_accepted_at) : null,
          contract_signed_at: placementData.contract_signed_at ? new Date(placementData.contract_signed_at) : null,
          contract_sent_at: placementData.contract_sent_at ? new Date(placementData.contract_sent_at) : null,

          offer_information: placementData.offer_information || '',
          related_offer_id: '',
        });

        setSelectedApplication(placementData.application.id || '');
      }
    }
  }, [isEditMode, placementData, reset]);

  const isLoading = isLoadingCandidates;
  const isSubmittingForm = isCreatingPlacement || isUpdatingPlacement;

  const linkableCompanies = useMemo(() => {
    if (!companies) return [];

    return companies.results.map((company) => ({
      id: company.id,
      company_name: company.company_name,
    }));

  }, [companies]);

  const linkableContacts = useMemo(() => {
    if (!contacts) return [];

    return contacts.results.map((contact) => ({
      id: contact.id,
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email || '',
    }));
  }, [contacts]);

  const payRateOptions = useMemo(() => {

    if ([EmploymentType.CONTRACT].includes(employmentType as EmploymentType)) {
      return [
        PeriodType.HOUR,
        PeriodType.DAY
      ]
    }

    return Object.values(PeriodType);

  }, [employmentType]);

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle>
        {isEditMode
          ? t('jobs.detail.placements.edit_title')
          : t('jobs.detail.placements.create_title')}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {/* Application Selection - only in create mode */}
                {!isEditMode && (
                  <Grid xs={12}>
                    <FormControl fullWidth error={!!errors.job_application_id}>
                      <InputLabel id="application-select-label">
                        {t('jobs.detail.placements.form.application')}
                      </InputLabel>
                      <Select
                        labelId="application-select-label"
                        id="application-select"
                        name="job_application_id"
                        value={selectedApplication}
                        onChange={(e) => handleApplicationChange(e.target.value as string)}
                        label={t('jobs.detail.placements.form.application')}
                        disabled={isLoadingCandidates}
                      >
                        {candidatesData?.results.map((application) => (
                          <MenuItem key={application.id} value={application.id}>
                            {`${application.attached_profile.first_name} ${application.attached_profile.last_name} - ${application.job.title}`}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.job_application_id && (
                        <FormHelperText>{errors.job_application_id.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {/* Candidate Information */}
                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t('jobs.detail.placements.form.candidate_info')}
                  </Typography>
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="candidate_first_name"
                    label={t('jobs.detail.placements.form.first_name')}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="candidate_last_name"
                    label={t('jobs.detail.placements.form.last_name')}
                  />
                </Grid>

                <Grid xs={12}>
                  <RHFTextField
                    name="candidate_email"
                    disabled={isEditMode}
                    label={t('jobs.detail.placements.form.email')}
                  />
                </Grid>

                {/* Job Information */}
                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t('jobs.detail.placements.form.job_info')}
                  </Typography>
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="job_title"
                    label={t('jobs.detail.placements.form.job_title')}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFSelect
                    name="employment_type"
                    label={t('jobs.detail.placements.form.employment_type')}
                  >
                    {Object.values(EmploymentType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`enums.employment_type.${type}`)}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Grid>

                {/* Dates */}
                <Grid xs={12} md={employmentType !== EmploymentType.PERMANENT ? 4 : 12}>
                  <MobileDatePicker
                    label={t('jobs.detail.placements.form.start_date')}
                    value={watch('start_date')}
                    onChange={(date) => setValue('start_date', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.start_date,
                        helperText: errors.start_date?.message,
                      }
                    }}
                  />
                </Grid>

                {
                  employmentType !== EmploymentType.PERMANENT && (
                    <>
                      <Grid xs={12} md={4}>
                        <MobileDatePicker
                          label={t('jobs.detail.placements.form.end_date')}
                          value={watch('end_date')}
                          onChange={(date) => setValue('end_date', date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.end_date,
                              helperText: errors.end_date?.message,
                            }
                          }}
                        />
                      </Grid>

                      <Grid xs={12} md={4}>
                        <RHFTextField
                          name="duration_days"
                          label={t('jobs.detail.placements.form.duration')}
                          disabled
                        />
                      </Grid>
                    </>
                  )
                }

                {/* Financial Information */}
                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t('jobs.detail.placements.form.financial_info')}
                  </Typography>
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="base_salary"
                    label={t('jobs.detail.placements.form.base_salary')}
                    type="number"
                    inputProps={{ step: "0.01" }}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="additional_entitlements"
                    label={t('jobs.detail.placements.form.additional_entitlements')}
                    type="number"
                    inputProps={{ step: "0.01" }}
                  />
                </Grid>

                {
                  employmentType === EmploymentType.CONTRACT && (
                    <Grid xs={12}>
                      <RHFTextField
                        name="on_costs"
                        label={t('jobs.detail.placements.form.on_costs')}
                        type="number"
                        inputProps={{ step: "0.01" }}
                      />
                    </Grid>
                  )
                }

                <Grid xs={12} md={employmentType === EmploymentType.CONTRACT && payRate === PeriodType.HOUR ? 6 : 12}>
                  <RHFSelect
                    name="pay_rate"
                    label={t('jobs.detail.placements.form.pay_rate')}
                  >
                    {payRateOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`enums.rate_period_per.${type}`)}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Grid>

                {
                  employmentType === EmploymentType.CONTRACT && payRate === PeriodType.HOUR && (
                    <Grid xs={12} md={6}>
                      <RHFTextField
                        name="hours_per_day"
                        label={t('jobs.detail.placements.form.hours_per_day')}
                        type="number"
                        inputProps={{ step: "0.01" }}
                      />
                    </Grid>
                  )
                }

                <Grid xs={12} md={6}>
                  <RHFSelect
                    name="placement_fee_type"
                    label={t('jobs.detail.placements.form.placement_fee_type')}
                  >
                    {Object.values(FeeType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`enums.fee_type.${type}`)}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="placement_fee_value"
                    label={t('jobs.detail.placements.form.placement_fee')}
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2">
                            {placementFeeType === FeeType.PERCENTAGE ? '%' : '$'}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {
                  placementFeeType === FeeType.PERCENTAGE && employmentType !== EmploymentType.CONTRACT && (
                    <>
                      <Grid xs={12} md={isProratedFee ? 6 : 12}>
                        <RHFCheckbox name="is_prorated_fee" label={t('jobs.detail.placements.form.is_prorated_fee')} sx={{ pl: 2 }} />
                      </Grid>

                      {
                        isProratedFee && (
                          <Grid xs={12} md={6}>
                            <RHFTextField
                              name="proration_percentage"
                              label={t('jobs.detail.placements.form.proration_percentage')}
                              type="number"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography variant="body2">
                                      %
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        )
                      }
                    </>
                  )
                }

                <Grid xs={12}>
                  <RHFAutocomplete
                    fullWidth
                    disableClearable
                    name="currency"
                    label={t('jobs.detail.placements.form.currency')}
                    disabled={isLoadingCurrencies}
                    getOptionLabel={(option: Currency | string) =>
                      isString(option) ? option : option?.name
                    }
                    options={currencies ?? []}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionKey={(option) => (option as Currency).id as string}
                  />
                </Grid>

                <Grid xs={12} md={employmentType !== EmploymentType.CONTRACT ? 12 : 4}>
                  <RHFTextField
                    name="total_remuneration"
                    label={t('jobs.detail.placements.form.total_remuneration')}
                    type="number"
                    disabled
                    inputProps={{ step: "0.01" }}
                    helperText="Base salary + additional entitlements"
                  />
                </Grid>

                {
                  employmentType === EmploymentType.CONTRACT && (
                    <Grid xs={12} md={4} >
                      <RHFTextField
                        name="bill_rate"
                        label={t('jobs.detail.placements.form.bill_rate')}
                        type="number"
                        disabled
                        inputProps={{ step: "0.01" }}
                        helperText="Total remuneration + placement fee + on costs"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Iconify icon="eva:currency-usd-fill" width={16} height={16} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )
                }
                {
                  employmentType === EmploymentType.CONTRACT && profitCalculationType === ProfitCalculationType.MARGIN && (
                    <Grid xs={12} md={4}>
                      <RHFTextField
                        name="margin"
                        label={t('jobs.detail.placements.form.margin')}
                        type="number"
                        helperText="Margin based on bill rate"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={t('jobs.detail.placements.form.switch_to_markup')} arrow sx={{ cursor: 'pointer' }}>
                                <Iconify icon="icon-park:switch" width={16} height={16} onClick={() => setValue('profit_calculation_type', ProfitCalculationType.MARKUP)} />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                  )
                }

                {
                  employmentType === EmploymentType.CONTRACT && profitCalculationType === ProfitCalculationType.MARKUP && (
                    <Grid xs={12} md={4}>
                      <RHFTextField
                        name="margin"
                        label={t('jobs.detail.placements.form.bill_markup')}
                        type="number"
                        helperText="Markup based on bill rate"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={t('jobs.detail.placements.form.switch_to_margin')} arrow sx={{ cursor: 'pointer' }}>
                                <Iconify icon="icon-park:switch" width={16} height={16} onClick={() => setValue('profit_calculation_type', ProfitCalculationType.MARGIN)} />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                  )
                }

                {
                  employmentType === EmploymentType.CONTRACT && (
                    <Grid xs={12} md={12}>
                      <RHFTextField
                        name="billable_days"
                        label={t('jobs.detail.placements.form.billable_days')}
                        type="number"
                      />
                    </Grid>
                  )
                }

                <Grid xs={12} md={12}>
                  <RHFTextField
                    name="total_margin"
                    label={t('jobs.detail.placements.form.total_margin')}
                    type="number"
                    helperText="Total margin for the placement"
                    disabled
                  />
                </Grid>

                {
                  tenantType === TenantType.Recruiter && (
                    <>
                      <Grid xs={12}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" sx={{ mt: 2 }}>
                            {t('jobs.detail.placements.form.client_info')}
                          </Typography>
                          <Button
                            variant="text"
                            startIcon={<Iconify icon="eva:plus-fill" />}
                            onClick={handleAddNewCompany}
                          >
                            {t('jobs.detail.placements.form.add_company')}
                          </Button>
                        </Stack>
                      </Grid>

                      <Grid xs={12}>
                        <RHFAutocomplete
                          name="employer_company"
                          label={t('jobs.detail.placements.form.employer_company.label')}
                          placeholder={t('jobs.detail.placements.form.employer_company.placeholder')}
                          multiple={false}
                          filterOptions={(x) => x}
                          loading={isCompaniesFetching}
                          onInputChange={(event, value) => {
                            onCompanySearchQueryChange(value);
                          }}
                          options={linkableCompanies as CompanyOption[] ?? []}
                          renderOption={(props, option: CompanyOption, { selected }) => (
                            <li {...props}>
                              <Stack direction="row">
                                <Typography variant="body1">{option.company_name}</Typography>
                              </Stack>
                            </li>
                          )}
                          getOptionLabel={(option: CompanyOption | string) =>
                            typeof option === 'string' ? option : option.company_name
                          }
                          isOptionEqualToValue={(option: CompanyOption, value: CompanyOption) => option.id === value.id}
                          getOptionKey={(option) => (option as CompanyOption).id as string}
                        />
                      </Grid>
                    </>
                  )
                }

                {
                  tenantType === TenantType.Client && (
                    <>
                      {/* recruiter information */}
                      <Grid xs={12}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" sx={{ mb: 2, mt: 2 }}>
                            {t('jobs.detail.placements.form.recruiter_info')}
                          </Typography>
                          <Button
                            variant="text"
                            startIcon={<Iconify icon="eva:plus-fill" />}
                            onClick={handleAddNewCompany}
                          >
                            {t('jobs.detail.placements.form.add_company')}
                          </Button>
                        </Stack>
                      </Grid>

                      <Grid xs={12}>
                        <RHFAutocomplete
                          name="recruiter_company"
                          label={t('jobs.detail.placements.form.recruiter_company.label')}
                          placeholder={t('jobs.detail.placements.form.recruiter_company.placeholder')}
                          multiple={false}
                          filterOptions={(x) => x}
                          loading={isCompaniesFetching}
                          onInputChange={(event, value) => {
                            onCompanySearchQueryChange(value);
                          }}
                          options={linkableCompanies as CompanyOption[] ?? []}
                          renderOption={(props, option: CompanyOption, { selected }) => (
                            <li {...props}>
                              <Stack direction="row">
                                <Typography variant="body1">{option.company_name}</Typography>
                              </Stack>
                            </li>
                          )}
                          getOptionLabel={(option: CompanyOption | string) =>
                            typeof option === 'string' ? option : option.company_name
                          }
                          isOptionEqualToValue={(option: CompanyOption, value: CompanyOption) => option.id === value.id}
                          getOptionKey={(option) => (option as CompanyOption).id as string}
                        />
                      </Grid>

                      <Grid xs={12} md={6}>
                        <RHFTextField
                          name="recruiter_first_name"
                          label={t('jobs.detail.placements.form.recruiter_first_name')}
                        />
                      </Grid>

                      <Grid xs={12} md={6}>
                        <RHFTextField
                          name="recruiter_last_name"
                          label={t('jobs.detail.placements.form.recruiter_last_name')}
                        />
                      </Grid>
                    </>
                  )
                }

                {/* Billing Information */}
                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t('jobs.detail.placements.form.billing_info')}
                  </Typography>
                </Grid>

                <Grid xs={12}>
                  <RHFAutocomplete
                    name="billing_contact"
                    label={t('jobs.detail.placements.form.billing_contact.label')}
                    placeholder={t('jobs.detail.placements.form.billing_contact.placeholder')}
                    multiple={false}
                    onInputChange={(event, value) => {
                      onContactSearchQueryChange(value);
                    }}
                    options={linkableContacts as ContactOption[] ?? []}
                    renderOption={(props, option: ContactOption, { selected }) => (
                      <li {...props}>
                        <Stack direction="row">
                          <Typography variant="body1">{option.first_name} {option.last_name}</Typography>
                        </Stack>
                      </li>
                    )}
                    getOptionLabel={(option: ContactOption | string) =>
                      typeof option === 'string' ? option : `${option.first_name} ${option.last_name}`
                    }
                    isOptionEqualToValue={(option: ContactOption, value: ContactOption) => option.id === value.id}
                    getOptionKey={(option) => (option as ContactOption).id as string}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="billing_contact_first_name"
                    label={t('jobs.detail.placements.form.billing_contact_first_name')}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="billing_contact_last_name"
                    label={t('jobs.detail.placements.form.billing_contact_last_name')}
                  />
                </Grid>

                <Grid xs={12}>
                  <RHFTextField
                    name="billing_contact_email"
                    type="email"
                    label={t('jobs.detail.placements.form.billing_contact_email')}
                  />
                </Grid>

                <Grid xs={12}>
                  <Divider />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="billing_invoice_number"
                    label={t('jobs.detail.placements.form.billing_invoice_number')}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="billing_po_number"
                    label={t('jobs.detail.placements.form.billing_po_number')}
                  />
                </Grid>

                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="payment_terms"
                    label={t('jobs.detail.placements.form.payment_terms')}
                  />
                </Grid>

                {
                  employmentType !== EmploymentType.CONTRACT && (
                    <Grid xs={12} md={6}>
                      <RHFSelect
                        name="payment_frequency"
                        label={t('jobs.detail.placements.form.payment_frequency')}
                      >
                        {Object.values(PaymentFrequency).map((type) => (
                          <MenuItem key={type} value={type}>
                            {t(`enums.payment_frequency.${type}`)}
                          </MenuItem>
                        ))}
                      </RHFSelect>
                    </Grid>
                  )
                }

                <Grid xs={12} md={6}>
                  <MobileDatePicker
                    label={t('jobs.detail.placements.form.send_invoice_on')}
                    value={watch('send_invoice_on')}
                    onChange={(date) => setValue('send_invoice_on', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>

                {/* Important Dates */}
                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {t('jobs.detail.placements.form.other_information')}
                  </Typography>
                </Grid>

                <Grid xs={12} md={12}>
                  <RHFTextField
                    name="offer_information"
                    label={t('jobs.detail.placements.form.offer_information')}
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid xs={12} md={4}>
                  <MobileDatePicker
                    label={t('jobs.detail.placements.form.offer_accepted_at')}
                    value={watch('offer_accepted_at')}
                    onChange={(date) => setValue('offer_accepted_at', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>

                <Grid xs={12} md={4}>
                  <MobileDatePicker
                    label={t('jobs.detail.placements.form.contract_sent_at')}
                    value={watch('contract_sent_at')}
                    onChange={(date) => setValue('contract_sent_at', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>

                <Grid xs={12} md={4}>
                  <MobileDatePicker
                    label={t('jobs.detail.placements.form.contract_signed_at')}
                    value={watch('contract_signed_at')}
                    onChange={(date) => setValue('contract_signed_at', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>


                {/* Custom Fields */}
                {customFields.hasCustomFields && (
                  <>
                    <Grid xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2 }}>
                        {t('common.custom_fields')}
                      </Typography>
                    </Grid>

                    <Grid xs={12}>
                      <Grid container gap={2}>
                        {customFields.renderCustomFields}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </FormProvider>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <LoadingButton
          variant="contained"
          loading={isSubmittingForm}
          onClick={handleSubmit(onSubmit)}
          disabled={!selectedApplication && !isEditMode}
        >
          {isEditMode ? t('common.update') : t('common.create')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  );
}